import React, { useEffect, useRef, useState } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  isComingSoon,
  getItemStatus,
  toJsDate,
  formatEpisodeLabel,
  isEpisodeConfirmed,
  isEpisodeMissing,
} from '../utils/dateUtils';
import { fetchAllMedia, fetchRecentlyAddedMedia, compactRecentlyAddedItems } from '../utils/mediaUtils';
import MediaCard from '../components/MediaCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

const CARD_WIDTH = 160;
const GRID_GAP = 20;
const GRID_EDGE_BLEED = 8;
const SECTION_HEADING_SPACE = 40;
const SECTION_VERTICAL_GAP = 40;
const MIN_SECTION_HEIGHT = 220;
const FALLBACK_CARD_HEIGHT = 320;

function getComingSoonSortDate(item) {
  if (item.type === 'movie') {
    const streaming = toJsDate(item.streamingReleaseDate);
    const dvd = toJsDate(item.dvdReleaseDate);
    if (streaming && dvd) return streaming < dvd ? streaming : dvd;
    if (streaming) return streaming;
    if (dvd) return dvd;
    return null;
  }
  return toJsDate(item.releaseDate);
}

function buildComingSoonItems(all, useLegacyEpisodeLists = false) {
  const now = new Date();

  return all.flatMap((item) => {
    const status = getItemStatus(item);
    if (status === 'missing') return [];

    if (item.type !== 'tv') {
      if (status === 'coming_soon') return [item];
      if (status === 'in_theatres') return [{ ...item, inTheatres: true }];
      return [];
    }

    const confirmedEpisodes = useLegacyEpisodeLists ? (item.confirmedEpisodes || []) : undefined;
    const missingEpisodes = useLegacyEpisodeLists ? (item.missingEpisodes || []) : undefined;
    const episodesWithDates = (item.episodes || []).filter((ep) => ep.airDate);
    const pendingEpisodeSource = episodesWithDates
      .filter((ep) => !isEpisodeConfirmed(ep, confirmedEpisodes) && !isEpisodeMissing(ep, missingEpisodes));

    const episodesBySeason = episodesWithDates.reduce((acc, ep) => {
      const seasonNumber = Number(ep.seasonNumber);
      if (!acc[seasonNumber]) acc[seasonNumber] = [];
      acc[seasonNumber].push(ep);
      return acc;
    }, {});

    const pendingBySeason = pendingEpisodeSource.reduce((acc, ep) => {
      const seasonNumber = Number(ep.seasonNumber);
      if (!acc[seasonNumber]) acc[seasonNumber] = [];
      acc[seasonNumber].push(ep);
      return acc;
    }, {});

    const completeSeasonNumbers = Object.keys(episodesBySeason)
      .map(Number)
      .filter((seasonNumber) => {
        const seasonEpisodes = episodesBySeason[seasonNumber] || [];
        const pendingSeasonEpisodes = pendingBySeason[seasonNumber] || [];
        return seasonEpisodes.length > 0 && pendingSeasonEpisodes.length === seasonEpisodes.length;
      });

    const allSeasonsComingSoon = completeSeasonNumbers.length > 0
      && completeSeasonNumbers.length === Object.keys(episodesBySeason).length;

    if (allSeasonsComingSoon) {
      const earliestEpisode = pendingEpisodeSource
        .map((ep) => toJsDate(ep.airDate))
        .filter(Boolean)
        .sort((a, b) => a - b)[0];
      const isOnlySeasonOne = completeSeasonNumbers.length === 1 && completeSeasonNumbers[0] === 1;

      return [{
        ...item,
        id: `${item.id}-all-seasons`,
        releaseDate: earliestEpisode || item.releaseDate,
        episodeLabel: isOnlySeasonOne ? 'Season 1' : 'All Seasons',
        showItem: item,
      }];
    }

    const completeSeasonSet = new Set(completeSeasonNumbers);
    const seasonCards = completeSeasonNumbers.map((seasonNumber) => {
      const seasonEpisodes = pendingBySeason[seasonNumber] || [];
      const earliestEpisode = seasonEpisodes
        .map((ep) => toJsDate(ep.airDate))
        .filter(Boolean)
        .sort((a, b) => a - b)[0];

      return {
        ...item,
        id: `${item.id}-season-${seasonNumber}`,
        releaseDate: earliestEpisode || item.releaseDate,
        episodeLabel: `Season ${seasonNumber}`,
        isEpisodeEntry: true,
        isAiredEpisode: earliestEpisode !== undefined && earliestEpisode !== null && earliestEpisode <= now,
        showItem: item,
      };
    });

    const episodeCards = pendingEpisodeSource
      .filter((ep) => !completeSeasonSet.has(Number(ep.seasonNumber)))
      .map((ep) => {
        const airDate = toJsDate(ep.airDate);
        return {
          ...item,
          id: `${item.id}-${ep.seasonNumber}-${ep.episodeNumber}`,
          releaseDate: ep.airDate,
          episodeLabel: formatEpisodeLabel(ep),
          isEpisodeEntry: true,
          isAiredEpisode: airDate !== null && airDate <= now,
          showItem: item,
        };
      });

    const pendingEpisodes = [...seasonCards, ...episodeCards];

    if (pendingEpisodes.length > 0) return pendingEpisodes;
    return isComingSoon(item) ? [item] : [];
  });
}

function Landing({ onItemClick, onNavigate }) {
  const [recentItems, setRecentItems] = useState([]);
  const [comingSoonItems, setComingSoonItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [columns, setColumns] = useState(1);
  const [contentWidth, setContentWidth] = useState(CARD_WIDTH);
  const [sectionHeight, setSectionHeight] = useState(MIN_SECTION_HEIGHT);
  const [sectionSlots, setSectionSlots] = useState(4);
  const containerRef = useRef(null);
  const firstComingSoonCardRef = useRef(null);
  const firstRecentCardRef = useRef(null);

  useEffect(() => {
    let active = true;

    const fetchRecentItems = async () => {
      const recent = await fetchRecentlyAddedMedia({ movieLimit: 100, episodeLimit: 160 });
      const compactedRecent = compactRecentlyAddedItems(recent);
      if (active) setRecentItems(compactedRecent);
    };

    const fetchComingSoonItems = async () => {
      const snap = await getDocs(
        query(collection(db, 'catalogue'), orderBy('releaseDate', 'desc'))
      );
      const all = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      let comingSoonSource = all;
      let useLegacyEpisodeLists = true;
      try {
        comingSoonSource = await fetchAllMedia();
        useLegacyEpisodeLists = false;
      } catch {
        comingSoonSource = all;
        useLegacyEpisodeLists = true;
      }

      const expandedComingSoon = buildComingSoonItems(comingSoonSource, useLegacyEpisodeLists);
      expandedComingSoon.sort((a, b) => {
        const dateA = getComingSoonSortDate(a);
        const dateB = getComingSoonSortDate(b);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA - dateB;
      });

      if (active) setComingSoonItems(expandedComingSoon);
    };

    const fetchData = async () => {
      const [recentResult, comingSoonResult] = await Promise.allSettled([
        fetchRecentItems(),
        fetchComingSoonItems(),
      ]);

      if (!active) return;

      const failed = [recentResult, comingSoonResult].some((result) => result.status === 'rejected');
      if (failed) {
        setError('Failed to load some landing data.');
        if (recentResult.status === 'rejected') console.error(recentResult.reason);
        if (comingSoonResult.status === 'rejected') console.error(comingSoonResult.reason);
      } else {
        setError('');
      }

      setLoading(false);
    };
    fetchData();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const recalcLayout = () => {
      const fallbackWidth = window.innerWidth >= 600 ? window.innerWidth - 48 : window.innerWidth - 32;
      const measuredWidth = containerRef.current?.getBoundingClientRect().width;
      const availableWidth = Math.max(0, Math.floor((measuredWidth || fallbackWidth) - (GRID_EDGE_BLEED * 2)));
      const nextColumns = Math.max(1, Math.floor((availableWidth + GRID_GAP) / (CARD_WIDTH + GRID_GAP)));
      const nextContentWidth = nextColumns * CARD_WIDTH + (nextColumns - 1) * GRID_GAP;

      const containerTop = containerRef.current?.getBoundingClientRect().top ?? 0;
      const availableHeight = Math.max(0, window.innerHeight - containerTop - 24);
      const nextSectionHeight = Math.max(
        MIN_SECTION_HEIGHT,
        Math.floor((availableHeight - SECTION_VERTICAL_GAP) / 2)
      );

      const measuredCardHeight = Math.max(
        firstComingSoonCardRef.current?.getBoundingClientRect().height || 0,
        firstRecentCardRef.current?.getBoundingClientRect().height || 0,
        FALLBACK_CARD_HEIGHT
      );
      const availableSectionContentHeight = Math.max(0, nextSectionHeight - SECTION_HEADING_SPACE);
      const nextRows = Math.max(1, Math.floor((availableSectionContentHeight + GRID_GAP) / (measuredCardHeight + GRID_GAP)));

      setColumns(nextColumns);
      setContentWidth(nextContentWidth);
      setSectionHeight(nextSectionHeight);
      setSectionSlots(Math.max(1, nextColumns * nextRows));
    };

    const rafId = window.requestAnimationFrame(recalcLayout);
    window.addEventListener('resize', recalcLayout);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', recalcLayout);
    };
  }, [loading, error, recentItems.length, comingSoonItems.length]);

  if (loading) return <LoadingSpinner message="Loading…" />;

  const showViewAllCard = sectionSlots > 1;
  const visibleItemLimit = Math.max(1, sectionSlots - (showViewAllCard ? 1 : 0));
  const visibleComingSoonItems = comingSoonItems.slice(0, visibleItemLimit);
  const visibleRecentItems = recentItems.slice(0, visibleItemLimit);

  const renderViewAllCard = (label, tab) => (
    <Card
      onClick={() => onNavigate && onNavigate(tab)}
      sx={{
        width: 160,
        height: 296,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          View All
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Card>
  );

  const sectionSx = {
    height: `${sectionHeight}px`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const gridSx = {
    width: '100%',
    maxWidth: `${contentWidth + (GRID_EDGE_BLEED * 2)}px`,
    mx: 'auto',
    px: `${GRID_EDGE_BLEED}px`,
    boxSizing: 'border-box',
    overflow: 'visible',
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, ${CARD_WIDTH}px)`,
    gap: 2.5,
    justifyContent: 'center',
    alignContent: 'start',
  };

  return (
    <Container ref={containerRef} maxWidth="xl" sx={{ py: 4, overflow: 'hidden' }}>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Coming Soon */}
      <Box sx={sectionSx}>
        <Typography variant="h5" sx={{ mb: 2 }}>Coming Soon</Typography>
        {comingSoonItems.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Typography color="text.secondary">Nothing coming soon yet.</Typography>
          </Box>
        ) : (
          <Box sx={gridSx}>
            {visibleComingSoonItems.map((item, index) => (
              <Box key={item.id} ref={index === 0 ? firstComingSoonCardRef : null}>
                <MediaCard
                  item={item}
                  subtitle={item.type === 'tv' ? (item.episodeLabel || '') : undefined}
                  onClick={() => onItemClick && onItemClick(item.showItem || item)}
                />
                {item.inTheatres ? (
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight={700}
                    sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                  >
                    🎬 In Theatres
                  </Typography>
                ) : item.isAiredEpisode ? (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    fontWeight={700}
                    sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                  >
                    📺 Aired
                  </Typography>
                ) : toJsDate(item.releaseDate) && (
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                  >
                    {toJsDate(item.releaseDate).toLocaleDateString('en-AU', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </Typography>
                )}
              </Box>
            ))}
            {showViewAllCard && renderViewAllCard('Coming Soon', 'comingSoon')}
          </Box>
        )}
      </Box>

      {/* Recently Added */}
      <Box sx={{ ...sectionSx, mt: 5 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Recently Added</Typography>
        {recentItems.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <Typography color="text.secondary">Nothing confirmed in the catalogue yet.</Typography>
          </Box>
        ) : (
          <Box sx={gridSx}>
            {visibleRecentItems.map((item, index) => (
              <Box key={item.id} ref={index === 0 ? firstRecentCardRef : null}>
                <MediaCard
                  item={item}
                  subtitle={item.type === 'tv' ? (item.episodeLabel || '') : undefined}
                  onClick={() => onItemClick && onItemClick(item.showItem || item)}
                />
                {toJsDate(item.releaseDate) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                  >
                    {toJsDate(item.releaseDate).toLocaleDateString('en-AU', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </Typography>
                )}
              </Box>
            ))}
            {showViewAllCard && renderViewAllCard('Recently Added', 'recentlyAdded')}
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default Landing;
